'use client';

import { ChevronRight } from 'lucide-react';
import { ReactNode, useEffect, useState } from 'react';

import { Collapsible, CollapsibleContent, Text } from '@/design-system/components/ui';

interface Props {
  defaultExpanded?: boolean;
  content: string | ReactNode;
  label: string;
  onExpand?: () => void;
  icon?: ((open?: boolean) => ReactNode) | ReactNode;
  align?: 'left' | 'right';
  isPrimary?: boolean;
  className?: string;
}

export default function CollapseItem({
  content,
  label,
  onExpand,
  defaultExpanded,
  icon,
  align,
  className,
  isPrimary,
}: Props) {
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded || false);

  useEffect(() => {
    if (isExpanded && onExpand) {
      onExpand();
    }
  }, [isExpanded]);

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <div className="tw:flex tw:flex-col tw:gap-4 tw:cursor-pointer">
        <div
          className={`tw:flex tw:flex-row tw:gap-4 tw:items-center ${align === 'left' ? 'tw:justify-start' : 'tw:justify-between'}`}
          onClick={() => setIsExpanded((prevState) => !prevState)}
        >
          <Text
            variant="body-md"
            weight="semibold"
            as="span"
            className={`tw:flex tw:gap-2 ${align === 'left' ? 'tw:flex-row-reverse' : 'tw:flex-row'} ${isPrimary ? 'tw:text-[var(--mui-palette-primary-main)]' : ''} ${className ?? ''}`}
          >
            {label}
            {icon ? (
              <span
                className={isPrimary ? 'tw:text-primary' : ''}
                style={{
                  display: 'inline-flex',
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s',
                }}
              >
                {typeof icon === 'function' ? icon(isExpanded) : icon}
              </span>
            ) : (
              <ChevronRight
                size={16}
                className={isPrimary ? 'tw:text-primary' : ''}
                style={{
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s',
                }}
              />
            )}
          </Text>
        </div>
        <CollapsibleContent>
          {typeof content === 'string' ? <Text variant="body-md">{content}</Text> : content}
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
